import { useState, useCallback, useRef, useEffect } from 'react'

import { ReactComponent as SplashOne } from '../../../../public/get-started/splash_1.svg'
import { ReactComponent as SplashTwo } from '../../../../public/get-started/splash_2.svg'
import { ReactComponent as SplashThree } from '../../../../public/get-started/splash_3.svg'

import { LoadingScreenSlideType } from '../types'

const SPLASH_DURATION_LIMIT_IN_MS = 33000
const TIME_PER_TICK_IN_MS = 250
const TIME_PER_SPLASH_SLIDE_IN_MS = 2400
const SPLASH_SLIDES: LoadingScreenSlideType[] = [
  {
    titleKey: 'get_started.splash_slides.one.title',
    descriptionKey: 'get_started.splash_slides.one.description',
    SVGComponent: SplashOne,
  },
  {
    titleKey: 'get_started.splash_slides.two.title',
    descriptionKey: 'get_started.splash_slides.two.description',
    SVGComponent: SplashTwo,
  },
  {
    titleKey: 'get_started.splash_slides.three.title',
    descriptionKey: 'get_started.splash_slides.three.description',
    SVGComponent: SplashThree,
  },
]

export const useSplashLoader = () => {
  const [
    { percentage, stepsCount, currentSlideIndex, isActive },
    setSplashLoadingState,
  ] = useState({
    isActive: false,
    percentage: 0,
    stepsCount: 0,
    currentSlideIndex: 0,
  })

  const animationStateRef = useRef<{
    startTimestamp?: number
    previousTickTimestamp?: number
    requestId: number
  }>()

  const startAnimation = useCallback(() => {
    const step = (timestamp: number) => {
      if (animationStateRef.current) {
        animationStateRef.current.startTimestamp =
          animationStateRef.current.startTimestamp || timestamp
        const animationDurationInMs =
          timestamp - animationStateRef.current.startTimestamp

        if (
          !animationStateRef.current.previousTickTimestamp ||
          (animationStateRef.current.previousTickTimestamp &&
            timestamp - animationStateRef.current.previousTickTimestamp >=
              TIME_PER_TICK_IN_MS)
        ) {
          const stepsCount = Math.ceil(
            animationDurationInMs / TIME_PER_SPLASH_SLIDE_IN_MS
          )
          const currentSlideIndex = (stepsCount - 1) % SPLASH_SLIDES.length

          setSplashLoadingState({
            isActive: true,
            percentage:
              (animationDurationInMs / SPLASH_DURATION_LIMIT_IN_MS) * 100,
            stepsCount,
            currentSlideIndex,
          })

          animationStateRef.current.previousTickTimestamp = timestamp
        }

        animationStateRef.current.requestId = window.requestAnimationFrame(step)
      }
    }

    animationStateRef.current = {
      requestId: window.requestAnimationFrame(step),
    }
  }, [])

  const stopAnimation = useCallback(() => {
    if (animationStateRef.current?.requestId) {
      window.cancelAnimationFrame(animationStateRef.current.requestId)

      setSplashLoadingState((prevSplashLoadingState) => ({
        ...prevSplashLoadingState,
        isActive: false,
      }))
    }
  }, [])

  useEffect(() => {
    return () => {
      if (animationStateRef.current?.requestId) {
        window.cancelAnimationFrame(animationStateRef.current.requestId)
      }
    }
  }, [])

  return {
    startAnimation,
    stopAnimation,
    wereAllSlidesPresented: stepsCount > SPLASH_SLIDES.length,
    stepsCount,
    percentage,
    isActive,
    currentSlide: SPLASH_SLIDES[currentSlideIndex],
  }
}
